import { Button, Dropdown, Field, makeStyles, Option, Text, tokens } from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { useState } from 'react';

import { HubApiError } from '@/api/client';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/design-system';
import { useEnvStore } from '@/env/envStore';
import { spacing } from '@/theme/tokens';

import { AI_TOOLS_WINDOW_OPTIONS, DEFAULT_AI_TOOLS_WINDOW, useAiTools, type AiToolsWindow } from './api';
import { ProviderSection } from './components/ProviderSection';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'space-between',
    gap: spacing.l,
    marginBottom: spacing.l,
  },
  refreshed: {
    color: tokens.colorNeutralForeground3,
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
});

const isWindow = (v: number): v is AiToolsWindow =>
  (AI_TOOLS_WINDOW_OPTIONS as readonly number[]).includes(v);

interface ProblemDetails {
  title?: string;
  detail?: string;
}

const isProblemDetails = (v: unknown): v is ProblemDetails =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function coerceProblemDetails(body: unknown): ProblemDetails | null {
  if (isProblemDetails(body)) {
    return body;
  }

  if (typeof body === 'string' && body.length > 0) {
    try {
      const parsed: unknown = JSON.parse(body);
      return isProblemDetails(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
}

function errorTitle(err: unknown): string {
  if (err instanceof HubApiError) {
    const details = coerceProblemDetails(err.body);
    if (details && typeof details.title === 'string') {
      return details.title;
    }
  }
  return 'Failed to load AI tools';
}

function errorDescription(err: unknown): string {
  if (err instanceof HubApiError) {
    const details = coerceProblemDetails(err.body);
    if (details && typeof details.detail === 'string') {
      return details.detail;
    }
    if (typeof err.body === 'string' && err.body.length > 0) {
      return err.body;
    }
  }
  return 'Try again, or check that the Anthropic and GitHub credentials are configured for the Corp environment.';
}

export function AiToolsPage() {
  const classes = useStyles();
  const env = useEnvStore((s) => s.env);
  const [days, setDays] = useState<AiToolsWindow>(DEFAULT_AI_TOOLS_WINDOW);
  const { data, isLoading, isError, error, refetch, isFetching } = useAiTools(days);

  // The route already redirects non-Corp users away, but guard defensively so
  // the page never queries the Corp-only endpoint from the wrong env.
  if (env !== 'corp') {
    return (
      <div>
        <PageHeader title="AI tools" description="Developer-AI tool spend." />
        <EmptyState
          title="Switch to the Corp environment"
          description="The AI tools spend dashboard is only available in the Corp environment. Use the environment switcher in the header."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="AI tools"
        description="Developer-AI tool spend — Claude (Anthropic) and GitHub Copilot — with month-to-date spend, projected month-end cost, and budget runway."
      />

      <div className={classes.toolbar}>
        <Field label="Window">
          <Dropdown
            value={`${String(days)} days`}
            selectedOptions={[String(days)]}
            onOptionSelect={(_, d) => {
              const v = Number(d.optionValue);
              if (!Number.isNaN(v) && isWindow(v)) {
                setDays(v);
              }
            }}
          >
            {AI_TOOLS_WINDOW_OPTIONS.map((opt) => (
              <Option key={opt} value={String(opt)}>
                {`${String(opt)} days`}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <div>
          <Text className={classes.refreshed} size={200}>
            {data
              ? `Generated ${new Date(data.generatedAtMs).toLocaleTimeString()} (server-side; cached ~1h)`
              : ''}
          </Text>
          <Button
            icon={<ArrowClockwiseRegular />}
            appearance="subtle"
            size="small"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingState label="Loading AI tools spend…" />
      ) : isError || !data ? (
        <ErrorState
          title={errorTitle(error)}
          description={errorDescription(error)}
          retry={() => void refetch()}
        />
      ) : data.providers.length === 0 ? (
        <EmptyState
          title="No providers configured"
          description="Configure Anthropic and GitHub credentials to see developer-AI tool spend."
        />
      ) : (
        <div className={classes.grid}>
          {data.providers.map((provider) => (
            <ProviderSection key={provider.providerKey} provider={provider} />
          ))}
        </div>
      )}
    </div>
  );
}
